import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import {
  ProductOrderSheetDiv,
  OrderSheetContainer,
  VariantTabs,
  Tab,
  BottomWrapper,
  InfoIcon,
  HorizontalSpacer,
  InheritedExtrasInfoContainer,
  Spacer,
} from './styled';
import ExtraSelector from './ExtraSelector';
import AllergenModal from './AllergenModal';
import { formatPrice } from '@/utils/formatter';
import { Row, IconButton, LabeledIconButton } from '../styled';
import VariantEditModal from './VariantEditModal';
import Button from '../../../../../components/Button';
import AddExtraGroupModal from './AddExtraGroupModal';
import {
  copyExtras as copyExtrasMutation,
  copyVariant as copyVariantMutation,
  updateProduct as updateProductMutation,
} from '../../../../../graphql/mutations';
import VariantCreateModal from './VariantCreateModal';
import { CopyPasteContext } from '../../../../../providers/CopyPasteContextProvider';
import Color from '@/utils/color';

function ProductOrderSheet({
  product,
  productCategoryMap,
  allExtrasForBrand,
  storeId,
  brandId,
  vatRates,
}) {
  const { variants } = product;

  const defaultVar = variants
    ? variants.find((v) => v.is_default).variant_id
    : null;
  const [selectedVariantId, setVariantId] = useState(defaultVar);
  const [updateProduct] = useMutation(updateProductMutation);
  const copyPasteContext = useContext(CopyPasteContext);

  let selectedVariantObj = null;
  let extraGroups = product.extra_groups;
  if (selectedVariantId && product.variants) {
    selectedVariantObj = product.variants.find(
      (variant) => variant.variant_id === selectedVariantId,
    );
    if (selectedVariantObj && Array.isArray(selectedVariantObj.extra_groups)) {
      extraGroups = selectedVariantObj.extra_groups;
    }
  }

  const extraItems = (extraGroups || []).reduce(
    (acc, group) => [...acc, ...group.items],
    [],
  );
  const defaultExtraIds = (extraGroups || []).reduce(
    (acc, group) => [...acc, ...(group.default_extras || [])],
    [],
  );
  const defaultExtras = defaultExtraIds.map((id) =>
    extraItems.find((item) => item.extra_id === id),
  );
  const [selectedExtras, setExtras] = useState(defaultExtras);
  const [infoModalOpen, setModalOpen] = useState(false);
  const [isAddExtraGroupModalOpen, setAddExtraGroupModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState(false);
  const [creatingVariant, setCreatingVariantModalOpen] = useState(false);
  const variantTabRefs = useRef([]).current;

  const [copyExtras, { loading: updatingCopyExtras }] =
    useMutation(copyExtrasMutation);
  const [copyVariant, { loading: updatingCopyVariant }] =
    useMutation(copyVariantMutation);
  const selectedVariantInheritsExtras =
    selectedVariantObj && selectedVariantObj.has_inherited_extras;

  // extras can be defined in the variant itself or can be inherited from the product
  const productIdOfExtras = !selectedVariantInheritsExtras
    ? selectedVariantId || product.id
    : product.id;

  function onShowAddVariantModal() {
    setCreatingVariantModalOpen(true);
  }
  function closeCreateVariantModal() {
    setCreatingVariantModalOpen(false);
  }

  function toggleInfoModal() {
    setModalOpen((prevOpen) => !prevOpen);
  }

  function onAllergensModalClosed(newAllergens) {
    product.allergens = newAllergens;
    toggleInfoModal();
  }

  function toggleAddExtraGroupModal() {
    setAddExtraGroupModalOpen((prevOpen) => !prevOpen);
  }

  function onMakeExtrasUnique(productIdFrom, variantIdTo) {
    copyExtras({
      variables: {
        product_id_from: productIdFrom,
        variant_id_to: variantIdTo,
      },
    });
  }

  function onVariantChange(id, index) {
    if (selectedVariantId === id) return;

    requestAnimationFrame(() => {
      if (variantTabRefs[index]) {
        variantTabRefs[index].scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    });

    setVariantId(id);
  }

  function closeVariantModal() {
    setEditingVariant(null);
  }

  function removeVariant(variant) {
    const removable = window.confirm(
      `Are you sure you want to remove the variant "${variant.title}"?`,
    );
    if (removable) {
      let newDefaultVariantId = product.variants.find(
        (v) => v.is_default,
      ).variant_id;
      if (newDefaultVariantId === variant.variant_id) {
        // default variant needs to be changed
        const newDefaultVariantObj = product.variants.find(
          (v) => v.variant_id !== variant.variant_id,
        );
        newDefaultVariantId = (newDefaultVariantObj || {}).variant_id || '';
      }

      const newVariantIds = product.variants
        .filter((v) => v.variant_id !== variant.variant_id)
        .map((v) => v.variant_id);
      updateProduct({
        variables: {
          id: product.id,
          product: {
            variants: {
              default_variant: newDefaultVariantId,
              variant_ids: newVariantIds,
            },
          },
          onError: (err) => {
            // eslint-disable-next-line no-console
            console.error('variants updateProduct', err);
            // eslint-disable-next-line no-alert
            alert(err.reason ? err.reason : err);
          },
        },
      });
    }
  }

  function copyVariantToClipboard(variant) {
    copyPasteContext.setVariant(variant);
    copyPasteContext.setProductOfVariantId(product.id);
  }

  function onPasteVariantClick() {
    copyVariant({
      variables: {
        variant_id: copyPasteContext.variant.variant_id,
        from_product_id: copyPasteContext.productOfVariantId,
        to_product_id: product.id,
      },
    });
  }

  return (
    <ProductOrderSheetDiv>
      <VariantTabs>
        {variants &&
          variants.map((variant, index) => (
            <Tab
              key={variant.name}
              ref={(ref) => {
                variantTabRefs[index] = ref;
              }}
              title={variant.variant_id}
              active={selectedVariantId === variant.variant_id}
              onClick={() => onVariantChange(variant.variant_id, index)}
            >
              <Row>
                {`${(variant.title || '').toUpperCase()} ${formatPrice(
                  variant.price,
                )}`}
                <IconButton
                  onClick={() => setEditingVariant(variant)}
                  name="edit"
                />
                <IconButton
                  onClick={() => copyVariantToClipboard(variant)}
                  name="clipboard"
                  color={
                    copyPasteContext.variant &&
                    copyPasteContext.variant.variant_id === variant.variant_id
                      ? Color.Cyan
                      : null
                  }
                />
                <IconButton
                  onClick={() => removeVariant(variant)}
                  name="trash"
                  color="red"
                />
              </Row>
            </Tab>
          ))}
        <Tab>
          <Row>
            <LabeledIconButton name="plus" onClick={onShowAddVariantModal}>
              {' '}
              Add a new variant
            </LabeledIconButton>
            <Spacer />
            <Button
              small
              disabled={!copyPasteContext.variant}
              onClick={onPasteVariantClick}
              loading={updatingCopyVariant}
            >
              Paste
            </Button>
          </Row>
        </Tab>
      </VariantTabs>
      {!!editingVariant && (
        <VariantEditModal
          storeId={storeId}
          onClose={closeVariantModal}
          productId={product.id}
          variant={editingVariant}
          vatRates={vatRates}
        />
      )}
      {creatingVariant && (
        <VariantCreateModal
          storeId={storeId}
          onClose={closeCreateVariantModal}
          brandId={brandId}
          product={product}
          vatRates={vatRates}
        />
      )}
      <OrderSheetContainer>
        {selectedVariantInheritsExtras && (
          <InheritedExtrasInfoContainer>
            <b>Note:</b> The variant &quot;
            {selectedVariantObj.title}
            &quot; inherits the extras from the product. Changes you make here
            may be also visible in other variants of this product.
            <br />
            <br />
            <Button
              onClick={() =>
                onMakeExtrasUnique(product.id, selectedVariantObj.variant_id)
              }
              loading={updatingCopyExtras}
            >
              Make extras unique
            </Button>
          </InheritedExtrasInfoContainer>
        )}
        <ExtraSelector
          productId={productIdOfExtras}
          extraGroups={extraGroups}
          selectedExtras={selectedExtras}
          allExtrasForBrand={allExtrasForBrand}
          onChange={setExtras}
          storeId={storeId}
          brandId={brandId}
          productCategoryMap={productCategoryMap}
        />

        <BottomWrapper>
          <Button onClick={toggleAddExtraGroupModal}>
            Add new extra-group
          </Button>
          <HorizontalSpacer height={10} />
          <InfoIcon name="info" onClick={toggleInfoModal} />
        </BottomWrapper>

        {infoModalOpen && (
          <div>
            <AllergenModal
              productId={product.id}
              allergens={product.allergens}
              onClose={onAllergensModalClosed}
            />
          </div>
        )}

        {isAddExtraGroupModalOpen && (
          <AddExtraGroupModal
            storeId={storeId}
            productId={productIdOfExtras}
            onClose={toggleAddExtraGroupModal}
          />
        )}
      </OrderSheetContainer>
    </ProductOrderSheetDiv>
  );
}

ProductOrderSheet.propTypes = {
  product: PropTypes.object.isRequired,
  productCategoryMap: PropTypes.object.isRequired,
  allExtrasForBrand: PropTypes.arrayOf(PropTypes.object).isRequired,
  storeId: PropTypes.string.isRequired,
  brandId: PropTypes.string,
  vatRates: PropTypes.arrayOf(PropTypes.number).isRequired,
};
ProductOrderSheet.defaultProps = {
  brandId: null,
};

export default ProductOrderSheet;
